import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Brand } from "./Brand";
import { cn } from "@/shared/utils/cn";
import { Button, type ButtonProps } from "../shadcn/Button";
import { navigationState } from "@/core/routing/navigationPolicy";

export function PageHeaderAction({ className, ...props }: Omit<ButtonProps, "variant">) {
  return <Button {...props} variant="ghost" className={cn("min-h-11 text-primary [&>svg]:hidden", className)} />;
}

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}

export function PageHeader({
  title,
  description,
  actions,
  children,
}: PageHeaderProps) {
  const { businessId, locationId } = useParams();
  const home = businessId && locationId ? `/b/${businessId}/l/${locationId}` : "/";
  return (
    <header className="mx-auto w-full max-w-[960px] shrink-0 text-foreground">
      <div className="flex min-h-16 items-start gap-2 px-4 py-2 sm:gap-4 sm:px-[30px]">
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
          <Link to={home} state={navigationState("start")} aria-label="Froppio home" className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl">
            <Brand className="[&>span:first-child]:text-[45px]" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="break-words text-xl font-extrabold leading-tight">{title}</h1>
            {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {actions && <div className="flex min-h-[60px] shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children && <div className="px-4 pb-3 sm:px-[30px]">{children}</div>}
    </header>
  );
}
